import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  Text,
  ButtonGroup,
  Badge,
  HStack,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { FaBook, FaUser, FaUsers, FaCommentDots } from "react-icons/fa";
import { useUserContext } from "../../../../Contexts/userContext";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import { useStudentsContext } from "../../../../Contexts/StudentsContext";
import { ExportDataRequest } from "../../../../services/Dtos";
import useAxios from "../../../../hooks/useAxios";
import { useStyles } from "../../../../hooks/useStyles";
import useThemeColors from "../../../../hooks/useThemeColors";
import DataCustomizationCard from "./DataCustomizationCard";
import Cad from "../../../CustomUi/Cad";
import SectionHeader from "../../../CustomUi/SectionHeader";
import useIcons from "../../../../hooks/useIcons";
import CustomButton from "../../../CustomUi/CustomButton";
import CustomPopover from "../../../CustomUi/CustomPopover";
import SelectInput from "../../../CustomUi/SelectInput";
import TextInput from "../../../CustomUi/TextInput";
import CustomCheckBox from "../../../CustomUi/CustomCheckBox";
import CustomAlert from "../../../CustomUi/CustomAlert";
import useToaster from "../../../../hooks/useToaster";
import CustomStep from "../../../CustomUi/CustomStep";
import { StepsContent } from "../../../ui/steps";

const ExportData: React.FC = () => {
  const { user } = useUserContext();
  const { showSuccessToast } = useToaster();
  const { classes, levels } = useSchoolsContext();
  const { students } = useStudentsContext();
  const axiosClient = useAxios();
  const { scrollBar } = useStyles();
  const colors = useThemeColors();
  const icons = useIcons();

  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);

  // Form state
  const [classId, setClassId] = useState("");
  const [termId] = useState("");
  const [sessionId] = useState("");
  const [levelId, setLevelId] = useState("");
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [includeAcademicRecords, setIncludeAcademicRecords] = useState(true);
  const [includeBehavioralRecords, setIncludeBehavioralRecords] =
    useState(true);
  const [includePersonalInfo, setIncludePersonalInfo] = useState(true);
  const [includeGuardianInfo, setIncludeGuardianInfo] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const steps = [
    {
      title: "Select Students",
    },
    { title: "Choose Data" },
    { title: "Confirm & Export" },
  ];

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      const studentClass = classes.find((c) => c.id === student.currentClassId);
      const matchesSearch = `${student.firstName} ${student.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesClass = !classId || student.currentClassId === classId;
      const matchesLevel =
        !levelId || (studentClass && studentClass.levelId === levelId);

      return matchesSearch && matchesClass && matchesLevel;
    });
  }, [students, searchTerm, classId, levelId, classes]);

  const handleStudentCheckboxChange = (studentId: string) => {
    setStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAllStudents = () => {
    setStudentIds(filteredStudents.map((s) => s.id));
  };

  const handleDeselectAllStudents = () => {
    setStudentIds([]);
  };

  const isStep1Complete = studentIds.length > 0;
  const isStep2Complete =
    includeAcademicRecords ||
    includeBehavioralRecords ||
    includePersonalInfo ||
    includeGuardianInfo;

  if (user?.role !== 3) {
    return (
      <CustomAlert
        status="error"
        title="Access Denied"
        message="Only administrators can export data."
      />
    );
  }

  const handleExport = async () => {
    setLoading(true);
    setError(null);

    const requestBody: ExportDataRequest = {
      classId: classId || undefined,
      termId: termId || undefined,
      sessionId: sessionId || undefined,
      levelId: levelId || undefined,
      studentIds,
      includeAcademicRecords,
      includeBehavioralRecords,
      includePersonalInfo,
      includeGuardianInfo,
    };

    try {
      const response = await axiosClient.post(
        "DataExport/export",
        requestBody,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const date = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `export-${date}.json`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccessToast("Export successful. Your file is downloading.");

      setCurrentStep(1);
      setStudentIds([]);
    } catch (err) {
      setError("An unexpected error occurred during export.");
    } finally {
      setLoading(false);
    }
  };

  const getSelectedDataTypes = () => {
    const types = [];
    if (includeAcademicRecords) types.push("Academic Records");
    if (includeBehavioralRecords) types.push("Behavioral Records");
    if (includePersonalInfo) types.push("Personal Info");
    if (includeGuardianInfo) types.push("Guardian Info");
    return types;
  };

  const classOptions = classes.map((c) => ({
    label: c.className,
    value: c.id,
  }));

  const levelOptions = levels.map((l) => ({
    label: l.levelName,
    value: l.id,
  }));

  const isNextDisabled =
    (currentStep === 0 && !isStep1Complete) ||
    (currentStep === 1 && !isStep2Complete);

  return (
    <CustomStep
      steps={steps}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      isNextDisabled={isNextDisabled}
      size="xs"
    >
      <StepsContent index={0}>
        <Cad flex={1}>
          {/* Header */}
          <SectionHeader
            title="Select Students"
            subtitle="Use the filter button to select students by class or level."
            icon={icons.people.students}
          />
          <VStack gap={3} align="stretch" flexShrink={0}>
            <HStack
              gap={3}
              flexDir={["column", "row"]}
              align={["stretch", "center"]}
            >
              <TextInput
                label=""
                name="search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                startElement={<Icon as={icons.utility.search} />}
                placeholder="Search students in filtered list"
              />
              <HStack>
                <CustomPopover
                  w="300px"
                  header={<Text fontWeight="semibold">Student Filters</Text>}
                  trigger={
                    <CustomButton
                      size="sm"
                      variant="surface"
                      colorPalette={colors.schoolAccent.primary}
                      label="Filters"
                      icon={icons.utility.filter}
                    />
                  }
                >
                  <VStack gap={3}>
                    <SelectInput
                      label="Class"
                      name="classId"
                      value={classId}
                      onChange={(e) => setClassId(e.target.value)}
                      collection={classOptions}
                      placeholder="All Classes"
                    />
                    <SelectInput
                      label="Level"
                      name="levelId"
                      value={levelId}
                      onChange={(e) => setLevelId(e.target.value)}
                      collection={levelOptions}
                      placeholder="All Levels"
                    />
                  </VStack>
                </CustomPopover>
                <ButtonGroup size="sm">
                  <CustomButton
                    colorPalette={colors.schoolAccent.primary}
                    label="Select All"
                    onClick={handleSelectAllStudents}
                    variant="outline"
                  />
                  <CustomButton
                    colorPalette={colors.schoolAccent.primary}
                    label="Clear"
                    onClick={handleDeselectAllStudents}
                    variant="outline"
                  />
                </ButtonGroup>
              </HStack>
            </HStack>
            <Text fontSize="sm" color={colors.text.secondary}>
              Showing {filteredStudents.length} of {students.length} students
            </Text>
          </VStack>
          <Box
            overflowY="auto"
            borderTop="1px solid"
            borderColor={colors.border.primary}
            css={scrollBar}
          >
            {/* Students List */}
            <VStack align="stretch" gap={1}>
              {filteredStudents.map((student) => (
                <Flex
                  key={student.id}
                  align="center"
                  p={2}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => handleStudentCheckboxChange(student.id)}
                  bg={
                    studentIds.includes(student.id)
                      ? colors.background.tertiary
                      : "transparent"
                  }
                  _hover={{
                    bg: colors.background.tertiary,
                  }}
                >
                  <Box onClick={(e) => e.stopPropagation()}>
                    <CustomCheckBox
                      checked={studentIds.includes(student.id)}
                      onChange={() => handleStudentCheckboxChange(student.id)}
                    />
                  </Box>
                  <HStack justify="space-between" w="full" ml={4}>
                    <Text fontWeight="medium" fontSize="sm">
                      {student.firstName} {student.lastName}
                    </Text>
                    <Badge
                      variant="subtle"
                      colorPalette="gray"
                      fontSize="0.7em"
                    >
                      {classes.find((c) => c.id === student.currentClassId)
                        ?.className || "No class"}
                    </Badge>
                  </HStack>
                </Flex>
              ))}
            </VStack>
          </Box>
        </Cad>
      </StepsContent>

      <StepsContent index={1}>
        <Cad overflow={"auto"}>
          <SectionHeader
            title="Choose Data"
            subtitle={`Select the types of data to export for ${studentIds.length} student(s).`}
            icon={icons.main.subject}
          />
          <VStack gap={6} align="stretch">
            <DataCustomizationCard
              isChecked={includeAcademicRecords}
              onChange={setIncludeAcademicRecords}
              icon={FaBook}
              title="Academic Records"
              description="Grades, test scores, etc."
            />
            <DataCustomizationCard
              isChecked={includeBehavioralRecords}
              onChange={setIncludeBehavioralRecords}
              icon={FaCommentDots}
              title="Behavioral Records"
              description="Conduct and behavioral notes."
            />
            <DataCustomizationCard
              isChecked={includePersonalInfo}
              onChange={setIncludePersonalInfo}
              icon={FaUser}
              title="Personal Info"
              description="Student's personal details."
            />
            <DataCustomizationCard
              isChecked={includeGuardianInfo}
              onChange={setIncludeGuardianInfo}
              icon={FaUsers}
              title="Guardian Info"
              description="Parent/guardian details."
            />
          </VStack>
        </Cad>
      </StepsContent>

      <StepsContent index={2}>
        <Cad>
          <VStack gap={6} align="stretch">
            <SectionHeader
              title="Confirm Export"
              subtitle="Confirm the export of the selected data."
              icon={icons.utility.download}
            />
            <Box
              p={4}
              borderWidth="1px"
              borderRadius="lg"
              bg={colors.background.secondary}
            >
              <VStack align="start" gap={2}>
                <Text>
                  <strong>Students:</strong> {studentIds.length} selected
                </Text>
                <Text>
                  <strong>Data Types:</strong>{" "}
                  {getSelectedDataTypes().join(", ")}
                </Text>
              </VStack>
            </Box>
          </VStack>
          <HStack
            flexDirection={["column", "row"]}
            align={["stretch", "center"]}
            justify={"flex-end"}
            w="full"
          >
            <CustomButton
              my={3}
              colorPalette={colors.schoolAccent.primary}
              label="Export Data"
              icon={icons.utility.download}
              onClick={handleExport}
              loading={loading}
            />
          </HStack>
        </Cad>
      </StepsContent>
      {error && <CustomAlert status="error" title="Error" message={error} />}
    </CustomStep>
  );
};

export default ExportData;
